import { useEffect, useMemo, useState } from "react";
import { Box, Button, Layer, Select, Text } from "grommet";
import { fetchAllUsers } from "./api";
import { getProctorUsername } from "./sessionUtils";

const CLEAR_TRANSFER_VALUE = "null";

export default function TransferToProctor({ podSession, onTransfer }) {
  const [usernames, setUsernames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pendingUsername, setPendingUsername] = useState(null);
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadUsers() {
      setLoading(true);

      try {
        const users = await fetchAllUsers();
        if (cancelled) return;

        const names = [
          ...new Set(
            users.map((user) => getProctorUsername(user)).filter(Boolean),
          ),
        ].sort((a, b) => a.localeCompare(b));

        setUsernames(names);
      } catch (error) {
        console.error(error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadUsers();

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return usernames;

    const lower = searchTerm.toLowerCase();
    return usernames.filter((name) => name.toLowerCase().includes(lower));
  }, [searchTerm, usernames]);

  const hasPendingTransfer =
    podSession?.transferToProctor !== null &&
    podSession?.transferToProctor !== undefined &&
    podSession?.transferToProctor !== "" &&
    podSession?.transferToProctor !== CLEAR_TRANSFER_VALUE;

  const handleConfirm = async () => {
    if (!pendingUsername) return;

    setTransferring(true);

    try {
      await onTransfer(pendingUsername);
      setPendingUsername(null);
    } catch (error) {
      console.error(error);
    } finally {
      setTransferring(false);
    }
  };

  const handleCancel = () => {
    setPendingUsername(null);
  };

  const handleClearTransfer = async () => {
    setTransferring(true);

    try {
      await onTransfer(CLEAR_TRANSFER_VALUE);
      setPendingUsername(null);
    } catch (error) {
      console.error(error);
    } finally {
      setTransferring(false);
    }
  };

  return (
    <Box gap="small">
      <Text weight="bold">Transfer to Proctor</Text>
      <Text size="small">
        only use for transfers, not for claiming this session
      </Text>
      {hasPendingTransfer && (
        <Box direction="row" align="center" gap="small" wrap>
          <Text size="small">
            Pending transfer: {podSession.transferToProctor}
          </Text>
          <Button
            label="Clear"
            size="small"
            onClick={handleClearTransfer}
            disabled={transferring}
          />
        </Box>
      )}
      <Select
        placeholder={loading ? "Loading proctors..." : "Select a proctor"}
        options={filteredOptions}
        value=""
        onChange={({ option }) => setPendingUsername(option)}
        onSearch={setSearchTerm}
        searchPlaceholder="Search proctors"
        disabled={loading || transferring}
      />
      {pendingUsername && (
        <Layer
          position="center"
          modal
          onEsc={handleCancel}
          onClickOutside={handleCancel}
        >
          <Box pad="medium" gap="medium" width="medium">
            <Text weight="bold">Transfer session to {pendingUsername}?</Text>
            <Text size="small">
              This will assign this session to {pendingUsername} for proctoring.
            </Text>
            <Box direction="row" gap="small" justify="end">
              <Button
                label="Cancel"
                onClick={handleCancel}
                disabled={transferring}
              />
              <Button
                label="Confirm"
                primary
                color="black"
                onClick={handleConfirm}
                disabled={transferring}
              />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  );
}
